import { GitBranch } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Reveal, SectionHeading } from './Reveal';
import { useLang } from '../i18n';

const SUBS = [
    ['Branding', '/servicios/branding'],
    ['Digitalización', '/servicios/digitalizacion'],
    ['Automatización', '/servicios/automatizacion'],
    ['SEO y SEM', '/servicios/seo-sem'],
    ['Growth Partner', '/servicios/growth-partner'],
];

const Node = ({ title, url, schema, dark = false, onClick, to, testid }) => {
    const cls = `rounded-xl border px-4 py-3 text-left transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${dark ? 'bg-ink text-pastelcream border-ink' : 'bg-white text-ink border-ink/15 hover:border-crimson/50'}`;
    const inner = (
        <>
            <span className="block font-bold text-[12px] uppercase tracking-[0.14em]">{title}</span>
            <span className={`block font-ink text-[10px] mt-1 ${dark ? 'text-pastelcream/60' : 'text-ink/45'}`}>{url}</span>
        </>
    );
    if (to) {
        return (
            <Link to={to} title={`${url} | Schema: ${schema}`} className={cls} data-testid={testid}>
                {inner}
            </Link>
        );
    }
    return (
        <button onClick={onClick} title={`${url} | Schema: ${schema}`} className={cls} data-testid={testid}>
            {inner}
        </button>
    );
};

const Branch = ({ children }) => (
    <div className="flex flex-col items-center">
        <div className="w-px h-7 bg-ink/25" />
        {children}
    </div>
);

export default function SitemapMap({ onNav }) {
    const { t } = useLang();
    return (
        <section id="arquitectura" className="py-24 lg:py-32 bg-pastelblue/40 border-t border-ink/10" data-testid="arquitectura-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <SectionHeading eyebrow={t('map_eyebrow')} title={t('map_title')} />
                    <Reveal delay={0.1}>
                        <p className="max-w-md text-ink/70 leading-relaxed lg:text-right">
                            {t('map_intro')}
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.15}>
                    <div className="mt-16 overflow-x-auto pb-4">
                        <div className="min-w-[760px] flex flex-col items-center" data-testid="sitemap-tree">
                            <div className="flex items-center gap-2 font-ink text-[10px] uppercase tracking-[0.3em] text-crimson mb-4">
                                <GitBranch size={14} />
                                {t('map_ia')}
                            </div>

                            <div className="w-64">
                                <Node title={t('map_inicio')} url="/ (home)" schema="WebSite + Organization" dark testid="sitemap-node-inicio" onClick={() => onNav('#inicio')} />
                            </div>

                            <div className="w-px h-8 bg-ink/25" />

                            <div className="grid grid-cols-4 gap-6 w-full items-start pt-0">
                                <Branch>
                                    <Node title={t('map_servicios')} url="/servicios" schema="CollectionPage + Service" to="/servicios" testid="sitemap-node-servicios" />
                                    <div className="mt-5 flex flex-col gap-3 w-44">
                                        {SUBS.map(([nm, u]) => (
                                            <Link key={u} to={u} className="rounded-lg border border-dashed border-ink/20 bg-white/60 px-3 py-2.5 hover:border-crimson/50 hover:bg-white transition-all" title={`${u} | Schema: Service + Offer`} data-testid={`sitemap-node-subservicio-${u.split('/').pop()}`}>
                                                <span className="block text-[11px] font-bold uppercase tracking-[0.1em] text-ink">{nm}</span>
                                                <span className="block font-ink text-[9px] text-ink/45 mt-0.5">{u}</span>
                                            </Link>
                                        ))}
                                    </div>
                                </Branch>

                                <Branch>
                                    <Node title={t('map_casos')} url="/casos-de-exito" schema="ItemList + Review" testid="sitemap-node-casos" onClick={() => onNav('#casos')} />
                                </Branch>

                                <Branch>
                                    <div className="flex flex-col gap-3 w-44">
                                        <Node title={t('map_historia')} url="/historia" schema="AboutPage" testid="sitemap-node-historia" onClick={() => onNav('#historia')} />
                                        <Node title={t('nav_mision')} url="/historia#mision" schema="AboutPage" testid="sitemap-node-mision" onClick={() => onNav('#mision')} />
                                        <Node title={t('nav_vision')} url="/historia#vision" schema="AboutPage" testid="sitemap-node-vision" onClick={() => onNav('#vision')} />
                                    </div>
                                </Branch>

                                <Branch>
                                    <Node title={t('map_agenda')} url="/agenda" schema="ContactPage + Action" testid="sitemap-node-agenda" onClick={() => onNav('#agenda')} />
                                </Branch>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <p className="mt-10 text-center font-ink text-[10px] uppercase tracking-[0.25em] text-ink/40" data-testid="sitemap-note">
                        {t('map_note')}
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
